import { useEffect, useRef } from "react";

type Scrollable = Pick<
  HTMLElement,
  "scrollTop" | "scrollHeight" | "clientHeight"
>;

function ratioOf(el: Scrollable) {
  const max = el.scrollHeight - el.clientHeight;
  return max > 0 ? el.scrollTop / max : 0;
}

function applyRatio(el: HTMLElement, ratio: number) {
  el.scrollTop = ratio * (el.scrollHeight - el.clientHeight);
}

/**
 * Links the editor pane and the preview iframe by scroll ratio so both panes
 * show the same part of the document. The iframe reloads whenever a new preview
 * renders, so the ratio is re-applied on load.
 */
export function useSyncedScroll(
  editor: HTMLElement | null,
  iframe: HTMLIFrameElement | null,
) {
  const ratioRef = useRef(0);

  useEffect(() => {
    if (!editor || !iframe) {
      return;
    }

    // Whichever pane the user is scrolling owns the position until it settles,
    // otherwise the programmatic scroll bounces back as input.
    let driver: "editor" | "preview" | null = null;
    let releaseTimer: ReturnType<typeof setTimeout>;

    function claim(side: "editor" | "preview") {
      driver = side;
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => (driver = null), 120);
    }

    function previewScroller() {
      return (iframe?.contentDocument?.scrollingElement ??
        null) as HTMLElement | null;
    }

    function onEditorScroll() {
      if (driver === "preview" || !editor) {
        return;
      }

      claim("editor");
      ratioRef.current = ratioOf(editor);

      const preview = previewScroller();
      if (preview) {
        applyRatio(preview, ratioRef.current);
      }
    }

    function onPreviewScroll() {
      const preview = previewScroller();
      if (driver === "editor" || !preview || !editor) {
        return;
      }

      claim("preview");
      ratioRef.current = ratioOf(preview);
      applyRatio(editor, ratioRef.current);
    }

    let previewDoc: Document | null = null;

    function attachPreview() {
      previewDoc?.removeEventListener("scroll", onPreviewScroll);
      previewDoc = iframe?.contentDocument ?? null;
      previewDoc?.addEventListener("scroll", onPreviewScroll, {
        passive: true,
      });

      const preview = previewScroller();
      if (preview) {
        applyRatio(preview, ratioRef.current);
      }
    }

    editor.addEventListener("scroll", onEditorScroll, { passive: true });
    iframe.addEventListener("load", attachPreview);
    attachPreview();

    return () => {
      clearTimeout(releaseTimer);
      editor.removeEventListener("scroll", onEditorScroll);
      iframe.removeEventListener("load", attachPreview);
      previewDoc?.removeEventListener("scroll", onPreviewScroll);
    };
  }, [editor, iframe]);
}
